/**
 * Assistant panel — chat with the modeling copilot, plus management of its
 * custom tools (defined by the model or by hand): publish/unpublish to the
 * toolbar, run, delete. Settings target any OpenAI-compatible endpoint.
 */

import type { App } from '../app';

export function mountAssistantPanel(host: HTMLElement, app: App): void {
  const panel = document.createElement('div');
  panel.className = 'nv-side';
  panel.style.display = 'none';
  host.appendChild(panel);

  let showSettings = false;

  const render = (): void => {
    const open = app.activePanel === 'assistant';
    panel.style.display = open ? 'flex' : 'none';
    panel.classList.add('nv-chat');
    if (!open) return;
    const assistant = app.assistant;
    panel.innerHTML = '';

    const head = document.createElement('div');
    head.className = 'chat-head';
    head.innerHTML = '<h3>Assistant</h3>';
    const gear = document.createElement('button');
    gear.className = 'chat-mini';
    gear.textContent = showSettings ? 'chat' : 'setup';
    gear.addEventListener('click', () => {
      showSettings = !showSettings;
      render();
    });
    const clearBtn = document.createElement('button');
    clearBtn.className = 'chat-mini';
    clearBtn.textContent = 'clear';
    clearBtn.addEventListener('click', () => assistant.clear());
    head.append(gear, clearBtn);
    panel.appendChild(head);

    if (showSettings) {
      for (const [label, key, pw] of [
        ['Endpoint (OpenAI-compatible)', 'baseUrl', false],
        ['API key (stored locally)', 'apiKey', true],
        ['Model', 'model', false],
      ] as const) {
        const wrap = document.createElement('div');
        wrap.className = 'field';
        wrap.innerHTML = `<label>${label}</label>`;
        const input = document.createElement('input');
        input.type = pw ? 'password' : 'text';
        input.value = assistant.settings[key];
        input.addEventListener('change', () => {
          assistant.settings[key] = input.value;
          void assistant.saveSettings();
        });
        wrap.appendChild(input);
        panel.appendChild(wrap);
      }
      // Custom tools management.
      const h = document.createElement('h3');
      h.textContent = 'Custom tools';
      h.style.marginTop = '14px';
      panel.appendChild(h);
      const tools = app.toolbox.customTools;
      if (tools.length === 0) {
        panel.insertAdjacentHTML(
          'beforeend',
          '<div class="side-status">None yet — ask the assistant to define_tool one.</div>',
        );
      }
      for (const t of tools) {
        const row = document.createElement('div');
        row.className = 'chat-tool-row';
        row.innerHTML = `<span class="body-name" title="${t.description}">${t.name}</span>`;
        const pub = document.createElement('button');
        pub.className = 'chat-mini';
        pub.textContent = t.published ? 'unpublish' : 'publish';
        pub.addEventListener('click', () => void app.toolbox.setPublished(t.name, !t.published));
        const del = document.createElement('button');
        del.className = 'chat-mini danger';
        del.textContent = '✕';
        del.addEventListener('click', () => void app.toolbox.remove(t.name));
        row.append(pub, del);
        panel.appendChild(row);
      }
      return;
    }

    // Chat transcript.
    const log = document.createElement('div');
    log.className = 'chat-log';
    if (assistant.history.length === 0) {
      log.innerHTML =
        '<div class="chat-empty">Describe what to build or change — the assistant drives the same parametric tools as the UI, can write its own, and every step is undoable.<br><br>e.g. “make a 60×40×10 plate with 4 bolt holes and a centered boss”.</div>';
    }
    for (const entry of assistant.history) {
      const div = document.createElement('div');
      div.className = `chat-msg chat-${entry.role}`;
      div.textContent = entry.text;
      log.appendChild(div);
    }
    if (assistant.streamingText) {
      const s = document.createElement('div');
      s.className = 'chat-msg chat-assistant';
      s.textContent = assistant.streamingText;
      log.appendChild(s);
    } else if (assistant.busy) {
      const b = document.createElement('div');
      b.className = 'chat-msg chat-busy';
      b.textContent = `…working (round ${assistant.busyRound}/${assistant.maxRounds})`;
      log.appendChild(b);
    }
    panel.appendChild(log);
    log.scrollTop = log.scrollHeight;

    // Composer.
    const composer = document.createElement('div');
    composer.className = 'chat-composer';
    const input = document.createElement('textarea');
    input.placeholder = 'Ask for a part, a change, a pattern…';
    input.rows = 2;
    input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        const text = input.value;
        input.value = '';
        void assistant.send(text);
      }
    });
    const sendBtn = document.createElement('button');
    sendBtn.className = 'nv-primary';
    sendBtn.style.width = '64px';
    sendBtn.style.marginTop = '0';
    sendBtn.textContent = 'Send';
    sendBtn.disabled = assistant.busy;
    sendBtn.addEventListener('click', () => {
      const text = input.value;
      input.value = '';
      void assistant.send(text);
    });
    composer.append(input, sendBtn);
    panel.appendChild(composer);

    // Vision toggle: attach a viewport screenshot to outgoing messages.
    const eyeRow = document.createElement('label');
    eyeRow.className = 'chat-eye';
    const eye = document.createElement('input');
    eye.type = 'checkbox';
    eye.checked = assistant.attachView;
    eye.addEventListener('change', () => {
      assistant.attachView = eye.checked;
    });
    eyeRow.append(eye, document.createTextNode(' 📷 attach view (vision models)'));
    panel.appendChild(eyeRow);
  };

  app.onUI(render);
  app.assistant.onChange = () => {
    render();
    app.notifyUI(); // toolbar may need to reflect new published tools
  };
  render();
}
